import { COPILOT_CONSTANTS } from '../constants';

/**
 * Ticket Pattern interface for type safety
 */
export interface TicketPattern {
  name: string;
  description: string;
  pattern: RegExp;
  normalize: (match: string) => string;
}

/**
 * Result of ticket extraction
 */
export interface TicketExtractionResult {
  ticketNumber: string | null;
  patternName: string | null;
  allMatches: Array<{ ticketNumber: string; patternName: string }>;
}

/**
 * Extracts text content from an Adaptive Card response
 */
const extractTextFromAdaptiveCard = (responseData: any): string => {
  if (!responseData || typeof responseData !== 'object') {
    return '';
  }

  if (responseData.type !== 'AdaptiveCard' || !Array.isArray(responseData.body)) {
    return '';
  }

  
  const textBlocks = responseData.body
    .filter((item: any) => item.type === 'TextBlock' && item.text)
    .map((item: any) => item.text)
    .join(' ');

  return textBlocks;
};

/**
 * Checks if the response indicates a ticket creation event
 */
const isTicketCreationResponse = (textToSearch: string, responseData?: any): boolean => {
  const ticketCreationIndicators = [
    'ticket has been created',
    'ticket has been submitted',
    'ticket created successfully',
    'request has been submitted',
    'incident has been created'
  ];

  const hasCreateTicketSkill = responseData?.invokedSkill === 'CreateTicket';
  const hasCreationIndicator = ticketCreationIndicators.some(
    indicator => textToSearch.toLowerCase().includes(indicator)
  );

  return hasCreateTicketSkill || hasCreationIndicator;
};

/**
 * Extracts ticket number from response text using configured patterns.
 * Patterns are checked in order defined in COPILOT_CONSTANTS.TICKET_PATTERNS.
 * Returns the first match found.
 * 
 * @param responseText - The text to search for ticket numbers
 * @param responseData - Optional response data (may contain Adaptive Card)
 * @returns TicketExtractionResult with ticket info or null values if not found
 */
export const extractTicketFromResponse = (
  responseText: string,
  responseData?: any
): TicketExtractionResult => {
  let textToSearch = responseText || '';

  // Extract text from Adaptive Card if present
  const cardText = extractTextFromAdaptiveCard(responseData);
  if (cardText) {
    textToSearch = cardText;
  }

  const result: TicketExtractionResult = {
    ticketNumber: null,
    patternName: null,
    allMatches: []
  };

  if (!textToSearch) {
    return result;
  }

  // Get patterns from configuration - cast to mutable array for iteration
  const patterns = COPILOT_CONSTANTS.TICKET_PATTERNS as unknown as TicketPattern[];

  // Iterate through all configured patterns
  for (const ticketPattern of patterns) {
    // Create a new RegExp instance to reset lastIndex for global patterns
    const regex = new RegExp(ticketPattern.pattern.source, ticketPattern.pattern.flags);
    const matches = textToSearch.match(regex);

    if (matches && matches.length > 0) {
      const normalizedMatch = ticketPattern.normalize(matches[0]);
      
      // Add to all matches
      result.allMatches.push({
        ticketNumber: normalizedMatch,
        patternName: ticketPattern.name
      });

      // Set first match as the primary result
      if (!result.ticketNumber) {
        result.ticketNumber = normalizedMatch;
        result.patternName = ticketPattern.name;
      }
    }
  }

  // Log if this looks like a ticket creation response but no ticket was found
  if (!result.ticketNumber && isTicketCreationResponse(textToSearch, responseData)) {
  } else if (!result.ticketNumber) {
  }

  return result;
};

/**
 * Simple extraction that returns just the ticket number string (backward compatible)
 */
export const extractTicketNumber = (responseText: string, responseData?: any): string | null => {
  return extractTicketFromResponse(responseText, responseData).ticketNumber;
};

/**
 * Validates if a string matches any configured ticket pattern
 */
export const isValidTicketNumber = (ticketId: string): boolean => {
  if (!ticketId) return false;

  const patterns = COPILOT_CONSTANTS.TICKET_PATTERNS as unknown as TicketPattern[];
  
  for (const ticketPattern of patterns) {
    const regex = new RegExp(`^${ticketPattern.pattern.source}$`, ticketPattern.pattern.flags.replace('g', ''));
    if (regex.test(ticketId)) {
      return true;
    }
  }
  
  return false;
};

/**
 * Gets the pattern name for a given ticket number
 */
export const getTicketPatternName = (ticketId: string): string | null => {
  if (!ticketId) return null;

  const patterns = COPILOT_CONSTANTS.TICKET_PATTERNS as unknown as TicketPattern[];
  
  for (const ticketPattern of patterns) {
    const regex = new RegExp(`^${ticketPattern.pattern.source}$`, ticketPattern.pattern.flags.replace('g', ''));
    if (regex.test(ticketId)) {
      return ticketPattern.name;
    }
  }
  
  return null;
};
